import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, Sparkles, Check, ShieldCheck, Truck, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { TrustBadges } from "@/components/TrustBadges";

const SELECT_POINTS = [
  "Hand-selected copy", "Surface inspected", "Corners inspected",
  "Edges inspected", "Centering reviewed", "Clear front/back photography", "Collector-ready protection",
];

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setActive(0); setQty(1); })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="bg-ink min-h-[60vh] flex flex-col items-center justify-center px-5">
      <p className="h-display text-3xl text-chrome mb-4">CARD NOT FOUND</p>
      <Link to="/shop" className="btn-holo rounded-lg px-6 py-3 text-sm">BACK TO SHOP</Link>
    </div>
  );
  if (!product) return <div className="bg-ink min-h-[60vh]" />;

  const images = product.images?.length ? product.images : [{ url: null, label: "front" }];
  const specs = [
    ["Player", product.player], ["Character", product.character],
    ["Year", product.year], ["Manufacturer", product.manufacturer],
    ["Set", product.card_set], ["Card #", product.card_number],
    ["Parallel / Variation", product.parallel], ["Condition", product.grade_label],
    ["SKU", product.sku],
  ].filter(([, v]) => v);

  return (
    <div className="bg-ink min-h-screen">
      <div className="mx-auto max-w-[1400px] px-5 py-10">
        <p className="text-xs text-[color:var(--chrome-3)] mb-8">
          <Link to="/" className="hover:text-white">Home</Link> /{" "}
          <Link to={`/shop/${product.category_slug}`} className="hover:text-white capitalize">{product.category_slug.replace("-", " ")}</Link> /{" "}
          <span className="text-[color:var(--chrome-2)]">{product.title}</span>
        </p>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <div className="rounded-3xl overflow-hidden bg-[color:var(--charcoal)] holo-glow aspect-[4/5] flex items-center justify-center">
              {images[active].url
                ? <img data-testid="product-main-image" src={images[active].url} alt={product.title} className="h-full w-full object-cover" />
                : <span className="text-[color:var(--chrome-3)]">No image</span>}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                    className={`h-20 w-20 rounded-xl overflow-hidden border-2 transition-colors ${active === i ? "border-[color:var(--cyan)]" : "border-[color:var(--line)]"}`}>
                    <img src={im.url} alt={im.label} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {product.cardscience_select && (
              <span className="select-badge inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold mb-4">
                <Sparkles size={12} /> CARDSCIENCE SELECT™
              </span>
            )}
            <h1 className="h-display text-3xl sm:text-4xl text-chrome">{product.title}</h1>
            <p className="text-[color:var(--chrome-2)] mt-3">
              {[product.year, product.manufacturer, product.card_set].filter(Boolean).join(" · ")}
              {product.card_number ? ` · #${product.card_number}` : ""}
            </p>

            <div className="flex items-end gap-4 mt-6">
              <span data-testid="product-price" className="text-4xl font-display font-black text-holo">${product.price.toFixed(2)}</span>
              {product.cardscience_select && product.reference_price != null && (
                <span className="text-sm text-[color:var(--chrome-3)] line-through mb-1">Ref ${product.reference_price.toFixed(2)}</span>
              )}
            </div>
            {product.cardscience_select && product.reference_price != null && (
              <p className="text-[11px] text-[color:var(--chrome-3)] mt-1">Reference market price is sample data and will be sourced from a live provider — never fabricated.</p>
            )}

            <p className="text-[color:var(--chrome-2)] leading-relaxed mt-6">{product.description}</p>

            <p className="text-xs font-tech tracking-wide mt-4 text-[color:var(--chrome-3)]">
              {product.quantity > 0 ? <span className="text-[color:var(--cyan)]">● IN STOCK · {product.quantity} available</span> : "SOLD OUT"}
            </p>

            {/* qty + add */}
            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center border border-[color:var(--line)] rounded-xl">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="p-3 text-white hover:text-[color:var(--cyan)]"><Minus size={16} /></button>
                <span data-testid="qty-value" className="w-10 text-center text-white font-semibold">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="p-3 text-white hover:text-[color:var(--cyan)]"><Plus size={16} /></button>
              </div>
              <button data-testid="pdp-add-to-cart" onClick={() => addItem(product, qty)}
                className="btn-holo flex-1 rounded-xl py-4 text-sm inline-flex items-center justify-center gap-2">
                <ShoppingCart size={17} /> ADD TO CART
              </button>
            </div>
            <button data-testid="pdp-buy-now" onClick={() => { addItem(product, qty); nav("/cart"); }}
              className="btn-ghost w-full rounded-xl py-4 text-sm mt-3">BUY NOW</button>

            {/* trust mini */}
            <div className="grid grid-cols-3 gap-3 mt-8">
              {[[Package, "The exact card you see"], [ShieldCheck, "Secure checkout"], [Truck, "Careful packaging"]].map(([Icon, t], i) => (
                <div key={i} className="flex flex-col items-center text-center gap-2 p-4 rounded-xl border border-[color:var(--line)]">
                  <Icon size={18} className="text-[color:var(--cyan)]" />
                  <span className="text-[10px] font-tech tracking-wide text-[color:var(--chrome-2)]">{t}</span>
                </div>
              ))}
            </div>

            {/* select checklist */}
            {product.cardscience_select && (
              <div className="mt-8 rounded-2xl border border-[color:var(--line)] p-6">
                <p className="eyebrow mb-4 flex items-center gap-2"><Sparkles size={12} className="text-[color:var(--cyan)]" /> What Select Includes</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {SELECT_POINTS.map((p) => (
                    <div key={p} className="flex items-center gap-2 text-sm text-[color:var(--chrome-1)]">
                      <Check size={14} className="text-[color:var(--cyan)]" /> {p}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Specs */}
        {specs.length > 0 && (
          <div className="mt-16 max-w-3xl">
            <h2 className="h-display text-2xl text-chrome mb-6">CARD DETAILS</h2>
            <div className="rounded-2xl border border-[color:var(--line)] overflow-hidden">
              {specs.map(([k, v], i) => (
                <div key={k} className={`flex justify-between px-6 py-4 ${i % 2 ? "bg-[color:var(--charcoal)]" : "bg-transparent"}`}>
                  <span className="text-sm text-[color:var(--chrome-3)]">{k}</span>
                  <span className="text-sm text-white font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-16">
        <TrustBadges />
      </div>
    </div>
  );
}
